
import * as angular from "angular";

import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { SettingsName } from "../class/SettingsName";
import { ConstantsService } from "./constantsService";

export class WebSocketService {

    connected = false;
    apiUrl: string;

    private listeners: { dataType: string, initialCommand: string, callback: (data: any) => void }[] = [];

    private webSocket: WebSocket;
    private autoReconnect;
    private loggingEnabled = false;

    private messageQueue: string[] = [];

    static $inject = ["$rootScope", "$q", "$timeout", "$window", "$interval", "constantsService"];

    constructor(
        private $rootScope: IRunBlockRootScope,
        private $q: angular.IQService,
        private $timeout: angular.ITimeoutService,
        private $window: angular.IWindowService,
        private $interval: angular.IIntervalService,
        private constantsService: ConstantsService)
    {
        this.apiUrl = this.constantsService.getApiUrl();
        // Auto enable logging on localhost
        //this.enableLogging(apiUrl.indexOf("fleet.") != 0);

        this.$interval(() => {
            this.ping();
        }, 60 * 1000);
    }

    enableLogging(enabled: boolean) {
        this.loggingEnabled = enabled;
    }

    open(autoReconnect = true): angular.IPromise<any> {

        return;
        this.autoReconnect = autoReconnect;

        let url = this.apiUrl.replace("http", "ws");
        if (url.indexOf("//") === 0) {
            url = `ws:${url}`;
        }

        //url += `/api/realtime/?accountId=${this.$rootScope.currentUser.AccountId}`;
        url += `/api/realtime/`;
        //console.log(url);

        const accessToken = this.$window.sessionStorage.getItem(SettingsName.AccessToken.toString());

        //console.log("accessToken", accessToken.length);

        this.webSocket = new WebSocket(url, accessToken);

        const defer = this.$q.defer();

        this.webSocket.onopen = (ev) => { this.onOpen(ev); defer.resolve(); };
        this.webSocket.onclose = (ev) => { this.onClose(ev); defer.reject(); };
        this.webSocket.onerror = (ev) => { this.onError(ev); defer.reject(); };
        this.webSocket.onmessage = (message) => { this.onMessage(message); };

        defer.promise.catch(angular.noop);

        return defer.promise;
    }

    close() {
        this.autoReconnect = false;

        if (this.webSocket) {
            this.webSocket.close();
            this.webSocket = null;
        }
    }

    send(message: string) {
        //this.webSocket.send(command);
        this.messageQueue.push(message);
        this.sendQueue();
    }

    registerListener<T>(dataType: "PlotDataStream" | "MessageDataStream", callback: (data: T) => void) {
        const command = `--command,subscribe:${dataType}`;

        this.send(command);

        this.listeners.push({
            dataType: dataType,
            initialCommand: command,
            callback: callback
        });
    }

    unregisterListener(dataType: "PlotDataStream" | "MessageDataStream") {
        //console.log(`Websocket unregister for ${dataType}...`);

        const command = `--command,unsubscribe:${dataType}`;
        this.send(command);

        let i = this.listeners.length;
        while (i--) {
            const listener = this.listeners[i];

            if (listener.dataType === dataType) {
                this.listeners.splice(i, 1);
            }
        }
    }

    private sendQueue() {
        if (!this.webSocket || this.webSocket.readyState !== 1) {
            //console.warn("Websocket not ready, delaying send");
            this.logWarning("Websocket not ready, delaying send");
            return;
        }

        if (this.messageQueue.length > 1) {
            this.logWarning(`WS - Sending backlog of ${this.messageQueue.length}`);
        }

        this.messageQueue.forEach(message => {
            //this.log("WS.Send", message);
            this.webSocket.send(message);
        });

        this.messageQueue = [];
    }

    private onMessage(message: MessageEvent) {

        const parsedData = this.getMessageDataType(message.data);

        const messageObj = JSON.parse(parsedData.data);

        this.listeners.forEach(listener => {
            if (listener.dataType == parsedData.dataType) {
                listener.callback(messageObj);
            }
        });
    }

    private getMessageDataType(message: string): { dataType: string, data: string } {
        if (message.indexOf("--dataType:") === 0) {
            const firstSemiColon = message.indexOf(":");
            const firstComma = message.indexOf(",");

            const dataType = message.substring(firstSemiColon + 1, firstComma);
            const data = message.substring(firstComma + 1, message.length);

            return { dataType: dataType, data: data };
        }

        return { dataType: null, data: message };
    }

    private onOpen(ev: Event) {
        // We always want to know when its connected
        this.log("WebSocket connected...");

        this.connected = true;
        this.sendQueue();

        // We should also re-subscribe to any listeners we set up
        this.listeners.forEach(listener => {
            this.send(listener.initialCommand);
        });
    }

    private onClose(ev: CloseEvent) {
        this.log("WebSocket Closed", ev);

        this.connected = false;

        if (this.autoReconnect) {
            const retryTimeout = 10 * 1000;

            this.$timeout(() => {
                this.log("WebSocket - Attempting auto reconnect");

                this.open(this.autoReconnect)
                    .then(() => {
                        this.listeners.forEach(listener => {
                            this.webSocket.send(listener.initialCommand);
                        });
                    })
                    .catch(() => {
                        this.logError("catch, closing socket");
                        if (this.webSocket) {
                            this.webSocket.close();
                        }
                    });
            }, retryTimeout);
        }
    }

    private ping() {
        if (!this.connected) {
            return;
        }

        this.send("--ping");
    }

    private onError(ev: Event) {
        this.connected = false;
        this.logError("WebSocket error", true, ev);
    }

    private log(message: any, ...optionalParams: any[]) {
        if (this.loggingEnabled) {
            console.log(message, optionalParams);
        }
    }

    private logWarning(message: any, ...optionalParams: any[]) {
        if (this.loggingEnabled) {
            console.warn(message, optionalParams);
        }
    }

    private logError(message: any, ...optionalParams: any[]) {
        if (this.loggingEnabled) {
            console.error(message, optionalParams);
        }
    }
}
